import {
  createContext, ReactNode, useContext, useEffect, useMemo, useState,
} from 'react';

import { INVENTORY_STATUS_OPTIONS } from 'containers/Inventory/constants';

import {
  getInventoryBrands,
  getInventoryUOMs,
  getInventoryCategories,
  getInventoryVendors,
  getInventoryAreas,
  getInventoryStrains,
  getInventoryAdjustReasons,
} from 'services/Inventory';

import { InventoryConditionItemName } from 'constants/enums';
import {
  InventoryBrand,
  InventoryUOM,
  InventoryCategory,
  InventoryVendor,
  InventoryArea,
  InventoryStrain,
} from 'types/customInventoryFilters.interface';
import { AdjustReason } from 'types/inventory.interface';
import { Option } from 'types/option.interface';
import { handleApiErrors } from 'utils/errorUtils';
import { getStoreId } from 'utils/storageUtils';

interface InventoryContextProps {
  brands: InventoryBrand[];
  strains: InventoryStrain[];
  areas: InventoryArea[];
  uoms: InventoryUOM[];
  categories: InventoryCategory[];
  vendors: InventoryVendor[];
  adjustReasons: AdjustReason[];
  getOptionsForItem: (name: InventoryConditionItemName) => Option[] | [];
}

const InventoryContext = createContext<InventoryContextProps>({} as InventoryContextProps);

export const InventoryProvider = ({ children }: { children: ReactNode }) => {
  const [brands, setBrands] = useState<InventoryBrand[]>([]);
  const [strains, setStrains] = useState<InventoryStrain[]>([]);
  const [areas, setAreas] = useState<InventoryArea[]>([]);
  const [uoms, setUOMs] = useState<InventoryUOM[]>([]);
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [vendors, setVendors] = useState<InventoryVendor[]>([]);
  const [adjustReasons, setAdjustReasons] = useState<AdjustReason[]>([]);

  async function fetchAndSet<T>(
    fetchFn: (clientId: string) => Promise<{ data: { list: T[] } }>,
    setFn: (data: T[]) => void,
    clientId: string,
  ) {
    try {
      const { data } = await fetchFn(clientId);
      setFn(data.list);
    } catch (e) {
      handleApiErrors(e);
    }
  }

  useEffect(() => {
    const clientId = getStoreId();
    if (!clientId) return;

    Promise.allSettled([
      fetchAndSet(getInventoryBrands, setBrands, clientId),
      fetchAndSet(getInventoryStrains, setStrains, clientId),
      fetchAndSet(getInventoryAreas, setAreas, clientId),
      fetchAndSet(getInventoryUOMs, setUOMs, clientId),
      fetchAndSet(getInventoryCategories, setCategories, clientId),
      fetchAndSet(getInventoryVendors, setVendors, clientId),
      fetchAndSet(getInventoryAdjustReasons, setAdjustReasons, clientId),
    ]);
  }, []);

  const getBrandsOption = (brands: InventoryBrand[]) => brands.filter((brand) => !brand.deleted).map((brand) => (
    { key: brand.brandName, value: brand.brandName }
  ));

  const getStrainsOption = (strains: InventoryStrain[]) => strains.map((strain) => (
    { key: strain.strainName, value: strain.strainName }
  ));

  const getAreasOption = (areas: InventoryArea[]) => areas.map((area) => (
    { key: area.areaPath, value: area.areaPath }
  ));

  const getVendorsOption = (vendors: InventoryVendor[]) => vendors.map((vendor) => (
    { key: vendor.vendorName, value: vendor.vendorName }
  ));

  const getUOMOption = (uoms: InventoryUOM[]) => uoms.map((uom) => (
    { key: uom.uomName, value: uom.uomName }
  ));

  const getCategoriesOption = (categories: InventoryCategory[]) => categories.filter(
    (category) => category.categoryPath,
  ).map((category) => (
    { key: category.categoryPath, value: category.categoryPath }
  ));

  const optionsMap: Partial<Record<InventoryConditionItemName, Option[]>> = {
    [InventoryConditionItemName.AREA]: getAreasOption(areas),
    [InventoryConditionItemName.STATUS]: INVENTORY_STATUS_OPTIONS,
    [InventoryConditionItemName.CATEGORY]: getCategoriesOption(categories),
    [InventoryConditionItemName.BRAND]: getBrandsOption(brands),
    [InventoryConditionItemName.VENDOR]: getVendorsOption(vendors),
    [InventoryConditionItemName.STRAIN]: getStrainsOption(strains),
    [InventoryConditionItemName.STORED_UOM]: getUOMOption(uoms),
  };

  const getOptionsForItem = (name: InventoryConditionItemName) => optionsMap[name] || [];

  const contextValue = useMemo(() => ({
    brands,
    strains,
    areas,
    uoms,
    categories,
    vendors,
    adjustReasons,
    getOptionsForItem,
  }), [brands, strains, areas, uoms, categories, vendors, adjustReasons]);

  return (
    <InventoryContext.Provider
      value={contextValue}
    >
      {children}
    </InventoryContext.Provider>
  );
};

export const useInventoryContext = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventoryContext must be used within an InventoryProvider');
  }
  return context;
};
